import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, FileText } from "lucide-react";
import { ProductVisual } from "./ProductVisual";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ProductCard = ({ product, index }) => {
  const { add, setOpen } = useCart();
  const packOptions = (product.pack_size || "").split("·").map((s) => s.trim()).filter(Boolean);
  const [pack, setPack] = useState(packOptions[0] || "");

  useEffect(() => {
    setPack(packOptions[0] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.pack_size]);

  const handleAdd = () => {
    add(product, packOptions.length > 1 ? pack : product.pack_size);
    toast.success(`${product.name}${packOptions.length > 1 ? ` (${pack})` : ""} added to quote list`);
  };

  const handleQuote = () => {
    add(product, packOptions.length > 1 ? pack : product.pack_size);
    setOpen(true);
  };

  return (
    <motion.div
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      style={{ contentVisibility: "auto", containIntrinsicSize: "0 420px" }}
      className="group relative border border-[#221533]/10 bg-white rounded-lg overflow-hidden flex flex-col hover:border-[#8E5A9E]/50 hover:shadow-[0_25px_60px_-20px_rgba(142,90,158,0.5)] hover:-translate-y-1.5 transition-[border-color,box-shadow,transform] duration-500"
    >
      <div className="relative aspect-[4/5] overflow-hidden spotlight bg-white cursor-zoom-in">
        {product.image_url ? (
          <img
            src={`${BACKEND}${product.image_url}?w=600`}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-contain group-hover:scale-[1.28] transition-transform duration-700 ease-out"
            style={{ imageRendering: "-webkit-optimize-contrast" }}
          />
        ) : (
          <ProductVisual
            type={product.visual}
            className="absolute inset-0 m-auto h-[72%] w-auto opacity-90 group-hover:scale-[1.28] transition-transform duration-700 ease-out"
          />
        )}
      </div>
      <div className="p-3.5 flex flex-col gap-1.5 flex-1 border-t border-[#221533]/10">
        <h3 className="font-clash text-base font-medium leading-tight text-[#221533]">{product.name}</h3>
        {packOptions.length <= 1 && (
          <p className="font-mono2 text-[10px] tracking-[0.12em] text-[#221533]/45 uppercase">{product.pack_size}</p>
        )}
        <p className="text-xs text-[#221533]/55 leading-relaxed line-clamp-2">{product.description}</p>
        {product.price && (
          <p data-testid={`product-price-${product.slug}`} className="text-sm font-bold text-[#74427F]">{product.price}</p>
        )}
        {packOptions.length > 1 && (
          <select
            data-testid={`packsize-select-${product.slug}`}
            value={pack}
            onChange={(e) => setPack(e.target.value)}
            className="mt-1 w-full bg-[#FAF8FC] border border-[#8E5A9E]/35 rounded-md px-2.5 py-2 text-xs font-manrope font-semibold text-[#74427F] focus:border-[#8E5A9E] transition-colors appearance-none cursor-pointer"
          >
            {packOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        )}
        <div className="mt-auto pt-3 flex gap-1.5">
          <button
            data-testid={`add-to-cart-${product.slug}`}
            onClick={handleAdd}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#8E5A9E] hover:bg-[#74427F] text-white text-[10px] font-semibold tracking-wide uppercase whitespace-nowrap px-2 py-3 rounded-md transition-colors duration-300 active:scale-[0.97]"
          >
            <Plus size={13} /> Add to Cart
          </button>
          <button
            data-testid={`request-quote-${product.slug}`}
            onClick={handleQuote}
            className="inline-flex items-center justify-center gap-2 border border-[#221533]/20 hover:border-[#8E5A9E] hover:text-[#74427F] text-[#221533]/70 text-xs font-semibold tracking-wide uppercase px-2.5 py-3 rounded-md transition-colors duration-300 active:scale-[0.97]"
            aria-label="Request bulk quote"
          >
            <FileText size={13} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};
